import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { styled } from 'styled-components';
import Text from '@ingka/text';
import Button from '@ingka/button';
import ContactForm, {
  type OutputData,
} from '@ikea-customer-self-serve/contact-form';
import { useTranslation } from 'react-i18next';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import ServiceValidationMsg from '../components/ServiceValidationMsg';
import useContact from '../hooks/useContact';
import { mediaQueries } from '../styles/mediaQueries';
import LoginLink from '../components/LoginLink';
import { shouldHideLoginLink } from '../helpers/loginLinkHelper';
import {
  addPersistingQueryParamsToPath,
  getPrevPath,
} from '../helpers/urlHelper';
import { isBusinessOperation } from '../helpers/operationsHelper';
import {
  isBusinessSwitchEnabled,
  isServiceProvidedAtHome,
} from '../helpers/appHelper';
import useContactFormFields from '../hooks/useContactFormFields';
import { useDebounce } from '../hooks/useDebounce';
import { useStateSelector } from '../store/';
import { ViewContext } from '../context';
import { isMinimalFormPreconditionsMet } from '../helpers/formHelper';
import StickyFooter from '../components/StickyFooter';
import AppFooter from '../components/AppFooter';
import { StyledWrapper } from '../components/PageLayout';
import {
  selectAppOperation,
  selectOperationForTheSelectedTimeSlot,
  selectServiceForTheSelectedTimeSlot,
} from '../reducers/selectors';
/* globals CSS_CONFIG */

const StyledText = styled(Text)`
  ${mediaQueries.portraitTabletUp} {
    &&& {
      margin-bottom: 1.5rem;
    }
  }
`;

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const view = useContext(ViewContext);
  const appOperation = useStateSelector(selectAppOperation);
  const customerType = useStateSelector((state) => state.user.customerType);
  const isLoggedIn = useStateSelector((state) => state.user.isLoggedIn);
  const allowedMemberDiscounts = useStateSelector(
    (state) => state.user.allowedMemberDiscounts,
  );
  const service = useStateSelector(selectServiceForTheSelectedTimeSlot);
  const operationForSelectedTimeSlot = useStateSelector(
    selectOperationForTheSelectedTimeSlot,
  );
  const [isBusiness, setIsBusiness] = useState(
    isBusinessOperation(operationForSelectedTimeSlot) ||
      (isLoggedIn && customerType === 'Business'),
  );
  const isViewAlt = view === 'alt';
  const isViewApp = view === 'app';
  const isPaidService = !!service && service.customerPrice.price > 0;
  const isAtHomeService = !service || isServiceProvidedAtHome(service.location);
  const showMinimalForm = isMinimalFormPreconditionsMet(
    isPaidService,
    isAtHomeService,
    isLoggedIn,
    CSS_CONFIG.minimalForm.enabled,
  );

  const [{ isBusy, serviceValidations, mandatoryQuestions }, { handleSubmit }] =
    useContact(appOperation);
  const [{ formState, disabledFields }] = useContactFormFields(
    operationForSelectedTimeSlot,
    showMinimalForm,
  );

  const debouncedHandleSubmit = useDebounce(
    (theFormData: unknown) => handleSubmit(theFormData as OutputData),
    300,
  );

  const handleBusinessChange = (showBusinessFields: boolean) => {
    setIsBusiness(showBusinessFields);
  };

  const showLoginLink =
    !isViewApp &&
    !isViewAlt &&
    !shouldHideLoginLink(
      isLoggedIn,
      allowedMemberDiscounts,
      customerType,
      isBusinessOperation(operationForSelectedTimeSlot),
      service?.displayPrice?.priceOfferType,
    );

  return (
    <>
      <PageTitle title={t('pages.contact.pageTitle')} />
      <StyledWrapper $withStickyFooter={isViewAlt || isViewApp}>
        <PageContent>
          <Text tagName="h2" textStyle="Heading.L">
            {isViewAlt
              ? t('pages.contact.plainViewTitle')
              : t('pages.contact.title')}
          </Text>
          {!isViewAlt && (
            <StyledText tagName="p" textStyle="Body.L">
              {t('pages.contact.description')}
            </StyledText>
          )}
          <ServiceValidationMsg errors={serviceValidations} />
          {showLoginLink && (
            <LoginLink
              service={service}
              showPromptCard={!mandatoryQuestions?.length}
            />
          )}
          <ContactForm
            locale={{
              country: CSS_CONFIG.country,
              language: CSS_CONFIG.language,
            }}
            operation={operationForSelectedTimeSlot}
            isBusiness={isBusiness}
            onIsBusinessChange={
              isLoggedIn ||
              isBusinessOperation(operationForSelectedTimeSlot) ||
              isViewAlt ||
              !isBusinessSwitchEnabled(service?.serviceId ?? '', CSS_CONFIG)
                ? undefined
                : handleBusinessChange
            }
            isBusy={isBusy}
            isLoggedIn={isLoggedIn}
            onSubmit={debouncedHandleSubmit}
            disabledFields={disabledFields}
            formData={formState}
            questionData={mandatoryQuestions}
            isPaidService={isPaidService}
            hideAddressFields={showMinimalForm}
            hideTaxFields={
              showMinimalForm && !!CSS_CONFIG.minimalForm?.hideTaxFields
            }
            id="contactForm"
          />
          {isViewAlt || isViewApp ? (
            <StickyFooter>
              {isViewApp && (
                <Button
                  onClick={() =>
                    navigate(
                      addPersistingQueryParamsToPath(
                        getPrevPath(appOperation, view),
                      ),
                    )
                  }
                  text={t('components.stickyFooter.previousButton')}
                  type="secondary"
                  disabled={isBusy}
                  data-cy="previous"
                  aria-label={t(
                    'components.stickyFooter.ariaLabelBackToTimeslots',
                  )}
                />
              )}
              <Button
                htmlType="submit"
                form="contactForm"
                id="nextButton"
                loading={isBusy}
                text={t('components.stickyFooter.bookButton')}
                type="emphasised"
                data-cy="next-button"
              />
            </StickyFooter>
          ) : (
            <AppFooter
              prevVisible
              nextVisible
              isLoading={isBusy}
              nextText={t('pages.contact.confirmBooking')}
              formId="contactForm"
              data-cy="next-button"
              nextAriaLabel={t('components.stickyFooter.ariaLabelToConfirm')}
              prevAriaLabel={t(
                'components.stickyFooter.ariaLabelBackToTimeslots',
              )}
            />
          )}
        </PageContent>
      </StyledWrapper>
    </>
  );
};

export default ContactPage;
